import { Injectable } from '@angular/core';
import { AngularFirestore, QueryFn } from '@angular/fire/compat/firestore';
import { Timestamp } from 'firebase/firestore';
import { firstValueFrom } from 'rxjs';

import UniqueEntityID from '../../../core/domain/unique_entity_id';
import { Goal, GoalStatus } from '../domain/goal/goal';

export interface GoalSchema {
  patientUserId?: string | null;
  description: string;
  status: GoalStatus;
  achieved: boolean;
  createdAt: Timestamp;
  updatedAt: Timestamp | null;
}

@Injectable()
export class GoalRepository {
  constructor(private firestore: AngularFirestore) {
    // Nada
  }

  private collection(patientId: string, queryFn?: QueryFn) {
    return this.firestore
      .collection<GoalSchema>('patients')
      .doc(patientId)
      .collection('goals', queryFn);
  }

  toSchema(goal: Goal): GoalSchema {
    return <GoalSchema>{
      patientUserId: goal.patientUserId ?? null,
      description: goal.description,
      status: goal.status,
      achieved: goal.achieved,
      createdAt: Timestamp.fromDate(goal.createdAt),
      updatedAt: goal.updatedAt ? Timestamp.fromDate(goal.updatedAt) : null,
    };
  }

  fromSchema(schema: GoalSchema, patientId: string, id?: string) {
    return Goal.create(
      {
        patientId,
        patientUserId: schema.patientUserId ?? undefined,
        description: schema.description,
        status: schema.status,
        achieved: schema.achieved,
        createdAt: schema.createdAt.toDate(),
        updatedAt: schema.updatedAt ? schema.updatedAt.toDate() : undefined,
      },
      new UniqueEntityID(id),
    );
  }

  async create(goal: Goal): Promise<Goal> {
    const schema = this.toSchema(goal);
    const created = await this.collection(goal.patientId).add(schema);
    return this.fromSchema(schema, goal.patientId, created.id);
  }

  async update(goal: Goal): Promise<Goal> {
    const schema = this.toSchema(goal);
    await this.collection(goal.patientId).doc(goal.id.toString()).set(schema);
    return this.fromSchema(schema, goal.patientId, goal.id.toString());
  }

  async findAll(patientId: string, status?: GoalStatus): Promise<Goal[]> {
    if (status) {
      const snap = await firstValueFrom(
        this.collection(patientId, (ref) =>
          ref.where('status', '==', status),
        ).get(),
      );
      return snap.docs.map((doc) =>
        this.fromSchema(doc.data() as GoalSchema, patientId, doc.id),
      );
    } else {
      const snap = await firstValueFrom(this.collection(patientId).get());
      return snap.docs.map((doc) =>
        this.fromSchema(doc.data() as GoalSchema, patientId, doc.id),
      );
    }
  }
}
